import { PageSpeedResult } from '../types';
import { getApiUrl } from '../config/environment';

export interface SavedAnalysis {
  id: string;
  url: string;
  timestamp: string; // ISO string format
  pageSpeedData: PageSpeedResult | null;
  llmAnalysis: string | null;
  title: string; // 사용자가 지정한 제목 또는 자동 생성된 제목
  batchResults?: any[]; // 다중 분석 결과 (선택적)
  urlLabels?: { [key: string]: string }; // URL별 표시 라벨 (다중 분석용)
}

// API 기본 URL 설정 - 백엔드 포트 3300으로 수정
const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? '/api'
  : 'http://localhost:3300/api';

// 인증 헤더를 포함한 기본 헤더 생성
const getAuthHeaders = () => {
  const token = localStorage.getItem('google_auth_token');
  console.log('🔐 토큰 확인:', token ? `존재 (길이: ${token.length})` : '없음');

  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
    console.log('✅ Authorization 헤더 설정됨');
  } else {
    console.warn('⚠️ 토큰이 없습니다 - localStorage 확인 필요');
  }

  return headers;
};

// 모든 저장된 분석 결과 가져오기
export const getSavedAnalyses = async (): Promise<SavedAnalysis[]> => {
  try {
    const headers = getAuthHeaders();
    console.log('🔐 API 호출 헤더:', headers);
    
    const response = await fetch(`${API_BASE_URL}/saved-analyses`, {
      headers: headers
    });
    
    console.log('📡 API 응답 상태:', response.status, response.statusText);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    console.log('📦 API 응답 데이터:', data);
    
    return data.success ? data.analyses : [];
  } catch (error) {
    console.error('❌ 저장된 분석 결과를 불러오는 중 오류:', error);
    return [];
  }
};

// 분석 결과 저장하기
export const saveAnalysis = async (
  url: string,
  pageSpeedData: PageSpeedResult | null,
  llmAnalysis: string | null,
  customTitle?: string,
  batchResults?: any[], // 다중 분석 결과 (선택적)
  urlLabels?: { [key: string]: string } // URL 라벨 (선택적)
): Promise<string> => {
  try {
    console.log('📤 saveAnalysis 호출:', {
      url,
      hasPageSpeedData: !!pageSpeedData,
      hasLlmAnalysis: !!llmAnalysis,
      customTitle,
      hasBatchResults: !!batchResults,
      batchResultsLength: batchResults?.length || 0,
      hasUrlLabels: !!urlLabels
    });

    const requestBody = {
      url,
      pageSpeedData,
      llmAnalysis,
      title: customTitle,
      batchResults, // 다중 분석 결과 포함
      urlLabels // URL 라벨 포함
    };

    console.log('📦 요청 본문:', requestBody);

    const response = await fetch(`${API_BASE_URL}/saved-analyses`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || '분석 결과 저장 실패');
    }

    return data.analysis.id;
  } catch (error) {
    console.error('분석 결과 저장 중 오류:', error);
    throw new Error('분석 결과를 저장할 수 없습니다.');
  }
};

// 특정 분석 결과 가져오기
export const getAnalysisById = async (id: string): Promise<SavedAnalysis | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/saved-analyses/${id}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) {
      if (response.status === 404) return null;
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.success ? data.analysis : null;
  } catch (error) {
    console.error('분석 결과 조회 중 오류:', error);
    return null;
  }
};

// 공개 분석 결과 가져오기 (인증 불필요)
export const getSharedAnalysisById = async (id: string): Promise<SavedAnalysis | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/shared-analysis/${id}`);
    if (!response.ok) {
      if (response.status === 404) return null;
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.success ? data.analysis : null;
  } catch (error) {
    console.error('공개 분석 조회 중 오류:', error);
    return null;
  }
};

// 분석 결과 삭제
export const deleteAnalysis = async (id: string): Promise<void> => {
  try {
    const response = await fetch(`${API_BASE_URL}/saved-analyses/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || '분석 결과 삭제 실패');
    }
  } catch (error) {
    console.error('분석 결과 삭제 중 오류:', error);
    throw new Error('분석 결과를 삭제할 수 없습니다.');
  }
};

// 분석 제목 수정
export const updateAnalysisTitle = async (id: string, newTitle: string): Promise<void> => {
  try {
    const response = await fetch(`${API_BASE_URL}/saved-analyses/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ title: newTitle })
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || '분석 제목 수정 실패');
    }
  } catch (error) {
    console.error('분석 제목 수정 중 오류:', error);
    throw new Error('분석 제목을 수정할 수 없습니다.');
  }
};

// 모든 저장된 분석 삭제
export const clearAllAnalyses = async (): Promise<void> => {
  try {
    const response = await fetch(`${API_BASE_URL}/saved-analyses`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || '전체 분석 삭제 실패');
    }
  } catch (error) {
    console.error('저장된 분석 전체 삭제 중 오류:', error);
    throw new Error('저장된 분석을 모두 삭제할 수 없습니다.');
  }
};

// 저장 용량 정보 가져오기
export const getStorageInfo = async () => {
  try {
    const analyses = await getSavedAnalyses();

    return {
      count: analyses.length,
      maxCount: 200, // 백엔드에서 설정한 최대값
      sizeKB: 0, // 백엔드 저장이므로 클라이언트에서 크기 측정 불필요
      maxSizeKB: 0 // 백엔드 저장이므로 제한 없음
    };
  } catch (error) {
    return {
      count: 0,
      maxCount: 200,
      sizeKB: 0,
      maxSizeKB: 0
    };
  }
};

// 데이터 내보내기 (JSON 파일로)
export const exportAnalyses = async (): Promise<void> => {
  try {
    const analyses = await getSavedAnalyses();
    const dataStr = JSON.stringify(analyses, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    
    const link = document.createElement('a');
    link.href = URL.createObjectURL(dataBlob);
    link.download = `url-analyzer-backup-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    
    URL.revokeObjectURL(link.href);
  } catch (error) {
    console.error('데이터 내보내기 중 오류:', error);
    throw new Error('데이터를 내보낼 수 없습니다.');
  }
};

// 데이터 가져오기 (JSON 파일에서) - 백엔드 저장이므로 단순화
export const importAnalyses = async (file: File): Promise<number> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = async (e) => {
      try {
        const data = JSON.parse(e.target?.result as string);
        
        if (!Array.isArray(data)) {
          throw new Error('올바르지 않은 파일 형식입니다.');
        }
        
        // 데이터 유효성 검사 - timestamp가 string이나 number 모두 허용
        const validAnalyses = data.filter((item: any) => 
          item.id && item.url && item.timestamp
        );
        
        if (validAnalyses.length === 0) {
          throw new Error('가져올 수 있는 유효한 데이터가 없습니다.');
        }
        
        // 각 분석을 개별적으로 저장 (백엔드에서 중복 처리)
        let importedCount = 0;
        for (const analysis of validAnalyses) {
          try {
            await saveAnalysis(
              analysis.url,
              analysis.pageSpeedData,
              analysis.llmAnalysis,
              analysis.title
            );
            importedCount++;
          } catch (error) {
            console.warn(`분석 ${analysis.id} 가져오기 실패:`, error);
          }
        }
        
        resolve(importedCount);
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = () => reject(new Error('파일을 읽을 수 없습니다.'));
    reader.readAsText(file);
  });
};